import React, { useState } from "react";
import { Button, Card, Col, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAppApi } from "@/App";
import { useRequest } from "@components/AppHook";
import { DevForm } from "@components/Forms/Fields";
import { RenderForm } from "@components/Forms/Render";
import { Awesome, DevModal } from "@components/Helpers";
import { DefaultLayout } from "@components/Layouts";
import { buildGetParamsFromApiData, buildQS, url_reverse } from "@components/Utils";

const FilterModal = ({ showFilterModal, setShowFilterModal, apiData, setApiData, dropCheckBoxes }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const filterAndClose = () => {
        setShowFilterModal(false);
        const getParams = buildGetParamsFromApiData({ apiData, prefix: "filter", filterEmptyStrings: true });
        navigate({ search: "?" + buildQS(getParams) });
        dropCheckBoxes(true);
    };

    return (
        <DevModal
            size="lg"
            show={showFilterModal}
            onHide={() => setShowFilterModal(false)}
            header={t("Фильтр")}
            body={
                <RenderForm
                    prefix="filter"
                    apiData={apiData}
                    setApiData={setApiData}
                    bareForm={false}
                    buttons={
                        <Button variant="primary" onClick={filterAndClose}>
                            {t("Выбрать игры")}
                        </Button>
                    }
                />
            }
        />
    );
};

export default function EventSelectionItemAddScreen() {
    const { t } = useTranslation();
    const { event_slug, selection_id } = useParams();
    const apiUrl = url_reverse("api:event:selection:item:add", { event_slug, selection_id });
    const { apiData, setApiData } = useAppApi(apiUrl);
    const navigate = useNavigate();
    const [showFilterModal, setShowFilterModal] = useState(false);
    const { api_get, api_post } = useRequest();
    const [isFetching, setIsFetching] = useState(null);
    const [showFinalList, setShowFinalList] = useState(false);
    const [finalListIds, setFinalListIds] = useState([]);
    const [finalListData, setFinalListData] = useState({});
    const [allSelected, setAllSelected] = useState(true);
    const [unselected, setUnselected] = useState([]);
    const [tooltipIsVisible, setTooltipIsVisible] = useState(false);

    const finalListUrl = url_reverse("api:event:selection:item:final_list", { event_slug, selection_id });

    const header = (
        <>
            <Link to={url_reverse("admin:event:items", { slug: event_slug })}>{t("Управление ивентом")}</Link>
            <span> – </span>
            {t("Добавление игр")}
        </>
    );

    const handleClearFilteredGame = () => {
        navigate(url_reverse("admin:event:selection:items:add", { event_slug, selection_id }));
    };

    const handleGameAddSubmit = (ev) => {
        const uniqueIdsList = Array.from(new Set(finalListIds)).join(",");
        api_post(ev, apiUrl, {
            params: { action: "add_games", game_ids: uniqueIdsList },
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiData(resp);
                    navigate(url_reverse("admin:event:items", { slug: event_slug }));
                }
            },
        });
    };

    const selectUnselectFilteredGame = (obj) => {
        if (unselected.includes(obj.id)) {
            setUnselected(unselected.filter((existingId) => existingId !== obj.id));
        } else {
            setUnselected([...unselected, obj.id]);
        }
    };

    const handleAllSelectedOrNot = (value) => {
        if (value) {
            setAllSelected(true);
            setUnselected([]);
        } else {
            setAllSelected(false);
            setUnselected(apiData.game_ids_added);
        }
    };

    const deleteFromFinalList = (obj) => {
        const newFinalListData = { ...finalListData };
        newFinalListData.object_list = newFinalListData.object_list.filter((game) => game.id !== obj.id);
        const newFinalListIds = finalListIds.filter((id) => id !== obj.id);
        setFinalListData(newFinalListData);
        setFinalListIds(newFinalListIds);
    };

    const fetchMoreFiltered = () => {
        const currentParams = buildGetParamsFromApiData({ apiData, prefix: "filter", filterEmptyStrings: true });
        const url = buildQS({ ...currentParams, page: apiData.pager?.number + 1 });

        setIsFetching("fetch-more");

        api_get(null, apiUrl + "?" + url, {
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiData((prevState) => {
                        return {
                            ...prevState,
                            object_list: apiData.object_list.concat(resp.object_list),
                            forms_data: resp.forms_data,
                            pager: resp.pager,
                            selection: resp.selection,
                            status: resp.status,
                        };
                    });
                }
            },
            finally: () => setIsFetching(null),
        });
    };

    const fetchMoreFinalList = () => {
        const url = buildQS({ page: finalListData.pager?.number + 1 });

        setIsFetching("fetch-more");
        api_post(null, finalListUrl + "?" + url, {
            params: { game_ids: finalListIds },
            then: (resp) => {
                if (resp.status === "ok") {
                    setFinalListData((prevState) => {
                        return {
                            ...prevState,
                            object_list: finalListData.object_list.concat(resp.object_list),
                            pager: resp.pager,
                            status: resp.status,
                        };
                    });
                }
            },
            finally: () => setIsFetching(null),
        });
    };

    const addGameToFinalList = () => {
        const uniqueIdsList = Array.from(new Set(apiData.game_ids_added));
        const filteredList = Array.from(uniqueIdsList).filter((id) => !unselected.includes(id));
        const combinedSet = new Set([...finalListIds, ...filteredList]);
        setFinalListIds(Array.from(combinedSet));
        if (filteredList.length > 0) {
            setTooltipIsVisible(true);
            setTimeout(() => setTooltipIsVisible(false), 5000);
        }
    };

    const loadFinalList = () => {
        api_post(undefined, finalListUrl, {
            params: { game_ids: finalListIds },
            then: (resp) => {
                if (resp.status === "ok") {
                    setFinalListData(resp);
                }
            },
        });
    };

    const finalListModal = () => {
        return (
            <DevModal
                size="lg"
                show={showFinalList}
                onHide={() => setShowFinalList(false)}
                header={
                    <Row>
                        <Col>{t("Игры")} </Col>
                        <Col className="text-right">
                            <DevForm.Button variant="dark" size="sm" name="save" onClick={handleGameAddSubmit}>
                                {t("Сохранить")}
                            </DevForm.Button>
                        </Col>
                    </Row>
                }
                body={
                    <>
                        <Table hover responsive className="dmg-table">
                            <thead>
                                <tr>
                                    <th width="120px">ID</th>
                                    <th>{t("Название")}</th>
                                    <th>{t("Разработчик")}</th>
                                    <th width="30px" />
                                </tr>
                            </thead>
                            <tbody>
                                {finalListData?.object_list?.map((o) => (
                                    <tr key={`filter${o.id}`}>
                                        <td>{o.id}</td>
                                        <td>{o.name}</td>
                                        <td>{o.developer}</td>
                                        <td>
                                            <Awesome
                                                type="trash-o"
                                                color="red"
                                                onClick={() => deleteFromFinalList(o)}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        {finalListData?.pager?.number < finalListData?.pager?.num_pages && (
                            <DevForm.Button
                                className="mt-3"
                                name="fetch-more"
                                onClick={fetchMoreFinalList}
                                isLoading={isFetching}
                            >
                                {t("Загрузить еще")}
                            </DevForm.Button>
                        )}
                    </>
                }
            />
        );
    };

    return (
        <DefaultLayout>
            <FilterModal
                showFilterModal={showFilterModal}
                setShowFilterModal={setShowFilterModal}
                apiData={apiData}
                setApiData={setApiData}
                dropCheckBoxes={handleAllSelectedOrNot}
            />
            {finalListModal()}
            {apiData && (
                <Card>
                    <Card.Header>
                        <Row>
                            <Col md="4">{header}</Col>
                            <Col md="2">
                                {t("Выбранные игры")}: {apiData?.game_ids_added?.length}
                            </Col>
                            <Col className="text-right">
                                {tooltipIsVisible && (
                                    <>
                                        <Awesome type="check" color="green" tooltip={t("Добавлено")} />
                                    </>
                                )}
                                <DevForm.Button
                                    variant="dark"
                                    size="sm"
                                    name="save"
                                    onClick={() => {
                                        loadFinalList();
                                        setShowFinalList(true);
                                    }}
                                >
                                    {t("Финальный список")}
                                </DevForm.Button>{" "}
                                <Button variant="dark" size="sm" onClick={() => setShowFilterModal(true)}>
                                    <Awesome type="filter" />
                                </Button>{" "}
                                <Button variant="dark" size="sm" onClick={handleClearFilteredGame}>
                                    {t("Очистить форму")}
                                </Button>{" "}
                                <DevForm.Button variant="dark" size="sm" name="save" onClick={addGameToFinalList}>
                                    {t("Добавить в финальный список")}
                                </DevForm.Button>
                            </Col>
                        </Row>
                    </Card.Header>
                    <Card.Body>
                        <Table hover responsive className="dmg-table">
                            <thead>
                                <tr>
                                    <th width="120px">ID</th>
                                    <th>{t("Название")}</th>
                                    <th>{t("Разработчик")}</th>
                                    <th width="30px">
                                        <DevForm.Checkbox
                                            checked={allSelected}
                                            onChange={() => handleAllSelectedOrNot(!allSelected)}
                                        />
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {apiData.object_list?.map((o) => (
                                    <tr key={`filter${o.id}`}>
                                        <td>{o.id}</td>
                                        <td>{o.name}</td>
                                        <td>{o.developer}</td>
                                        <td>
                                            <DevForm.Checkbox
                                                checked={!unselected.includes(o.id)}
                                                onChange={() => selectUnselectFilteredGame(o)}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        {apiData.pager?.number < apiData.pager?.num_pages && (
                            <DevForm.Button
                                className="mt-3"
                                name="fetch-more"
                                onClick={fetchMoreFiltered}
                                isLoading={isFetching}
                            >
                                {t("Загрузить еще")}
                            </DevForm.Button>
                        )}
                    </Card.Body>
                </Card>
            )}
        </DefaultLayout>
    );
}
